import React, { useCallback } from "react";
import styled from "styled-components";
import { FontWeight, borderRadius, transparentize } from "@glossy";
import { background, content, dominant, onDominant } from "@glossy/theme";

export const STATES = ["selected", "active", "hidden", "disabled", "default"];
export type State = typeof STATES[number];

interface IProps {
  state: State;
  onClick?: () => void;
  children: React.ReactNode;
  start?: boolean;
  end?: boolean;
  className?: string;
}

const ITEM_RADIUS = 15;

const Date = styled.div<{
  selected?: boolean;
}>`
  border: 1px solid transparent;
  background-color: ${(props) => props.selected && dominant(props)};
  border-radius: ${ITEM_RADIUS}px;
`;

const Square = styled.div<{
  disabled?: boolean;
  selected?: boolean;
  active?: boolean;
  isStart?: boolean;
  isEnd?: boolean;
  hidden: boolean;
}>`
  position: relative;
  display: inline-block;
  text-align: center;
  line-height: 28px;
  margin: 3px 0;
  min-width: 30px;
  color: ${(props) =>
    props.selected
      ? onDominant
      : props.disabled
      ? content(props, "tertiary")
      : content(props, "secondary")};
  opacity: ${(props) => props.hidden && 0};
  cursor: ${(props) => !props.disabled && !props.hidden && "pointer"};
  font-weight: ${(props) => props.selected && FontWeight.Bold};
  background-color: ${(props) =>
    props.active && transparentize(0.8, dominant(props))};
  ${(props) =>
    props.active && props.isStart && borderRadius("left", ITEM_RADIUS)};
  ${(props) =>
    props.active && props.isEnd && borderRadius("right", ITEM_RADIUS)};

  :hover {
    ${Date} {
      border-color: ${(props) =>
        props.selected
          ? background(props)
          : !props.disabled && !props.hidden
          ? dominant(props)
          : "none"};
    }
  }
`;

const useDate = (onClick, state) => {
  const isActive = state === "active" || state === "selected";
  const isSelected = state === "selected";

  const handleClick = useCallback(() => {
    if (state !== "hidden" && state !== "disabled") {
      onClick();
    }
  }, [onClick, state]);

  return { isActive, isSelected, handleClick };
};

const UiKitPeriodSelectorDate = ({
  onClick,
  start,
  end,
  state,
  children,
  className,
}: IProps) => {
  const { isActive, isSelected, handleClick } = useDate(onClick, state);

  return (
    <Square
      active={isActive}
      isStart={start}
      isEnd={end}
      selected={isSelected}
      onClick={handleClick}
      disabled={state === "disabled"}
      hidden={state === "hidden"}
      className={className}
    >
      <Date selected={isSelected}>{children}</Date>
    </Square>
  );
};

export default UiKitPeriodSelectorDate;
